import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { TGetItemFn, TSetItemFn, TPlan } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { CheckoutFlowVersion, PaymentPlatform } from '@/enums/payments'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { PageTitle } from '@/components/pageTitle'
import { PaymentScreenPolicy } from '@/components/policyLinkBlock'
import { Receipt } from '@/components/receipt'
import { TextBlock } from '@/components/textBlock'

import { useAuth } from '@/auth/useAuth'
import {
  paypalButtonMap,
  stripeCheckoutFormMap,
  walletPaymentButtonMap,
} from '@/commonPages/paymentScreenPage/componentMappings'
import { useProcessContactState } from '@/commonPages/paymentScreenPage/hooks/contactState/useProcessContactState'
import { TPaymentScreenPageOptions } from '@/commonPages/paymentScreenPage/types'
import { TSelectPlanBlockControl } from '@/commonPages/selectPlan/components/types'
import { useSession } from '@/core/session/useSession'
import { trackAddToCart } from '@/events/events'

const paypalOptions = getDefaultPayPalOptions()

export type TPaymentBlockProps = {
  getItem: TGetItemFn
  setItem: TSetItemFn
  config: IConfig
  plans: TPlan[]
  pageName: PageName
  selectedPlan: TPlan
  paymentOptions: TPaymentScreenPageOptions
}

export const PaymentBlock = forwardRef<TSelectPlanBlockControl, TPaymentBlockProps>(
  function PaymentBlock(
    { getItem, setItem, config, plans, pageName, selectedPlan, paymentOptions }: TPaymentBlockProps,
    ref
  ) {
    const { user } = useAuth()
    const { checkoutFlowVersion } = useSession()

    const {
      discount,
      title,
      paymentMethodTitle,
      receiptData,
      receiptTitle,
      stripeFormConfig,
      walletButtonConfig,
      paypalCustomTagline,
      subscriptionsDetails,
    } = paymentOptions.pageConfig

    const sessionContext = useSession()

    const inputRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(
      ref,
      () => {
        return {
          scrollIntoView() {
            inputRef.current?.scrollIntoView({ behavior: 'smooth' })
          },
        }
      },
      []
    )
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
      const sectionElement = inputRef.current
      if (!sectionElement) return

      const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
        threshold: 0.5,
      })

      observer.observe(sectionElement)

      return () => {
        if (sectionElement) {
          observer.unobserve(sectionElement)
        }
      }
    })

    useEffect(() => {
      if (!isVisible || getItem('AddToCardSent') === '1') return

      trackAddToCart(
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user
      )

      setItem('AddToCardSent', '1')
    }, [
      isVisible,
      config.PIXEL_ID,
      config.TWITTER_CONF,
      config.TIKTOK_PIXEL_ID,
      sessionContext,
      user,
      getItem,
      setItem,
    ])

    const showPayPal = useMemo<boolean>(
      () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
      [config]
    )
    const showStripe = useMemo<boolean>(
      () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
      [config]
    )

    const selectedPlanId = selectedPlan.planId
    const planData = useMemo(
      () => plans.find((plan) => plan.planId === selectedPlanId),
      [plans, selectedPlanId]
    )

    const stripeOptions = useMemo<StripeElementsOptions | undefined>(
      () => getDefaultStripeOptions(planData, paymentOptions.stripeFormAppearance),
      [planData, paymentOptions.stripeFormAppearance]
    )

    const className = `page page-${pageName} embedded-section`

    // TODO: after first test in prod consider to remove or add conditions
    // to avoid oversharing emails with partner
    const contactStateEl = useProcessContactState(
      config.PAGE_SET_ID,
      getItem,
      setItem,
      { className, stateRoutes: paymentOptions.contactStateRoutesMap },
      checkoutFlowVersion
    )
    if (contactStateEl) {
      return contactStateEl
    }

    if (!user && checkoutFlowVersion !== CheckoutFlowVersion.V2) {
      // TODO: probably there are better typescript hints to avoid this check here
      return <Navigate to={getPath(PageName.registration)} />
    }

    if (!planData || !stripeOptions) {
      return null
    }

    const successRedirectTo = paymentOptions.successRedirectTo || PageName.paymentSuccess
    const contactStateRoutesMap = paymentOptions.contactStateRoutesMap

    const WalletPaymentFC = walletPaymentButtonMap[checkoutFlowVersion]
    const PayPalButtonFC = paypalButtonMap[checkoutFlowVersion]
    const StripeCheckoutFormFC = stripeCheckoutFormMap[checkoutFlowVersion]

    return (
      <div className={className} ref={inputRef}>
        <PayPalScriptProvider options={{ ...paypalOptions }}>
          <Elements stripe={paymentOptions.stripePromise} options={stripeOptions}>
            <div className="offer">{title}</div>
            <PageTitle title={discount} />
            <div className="payment-method">{paymentMethodTitle}</div>
            <div className="floating-container ">
              {showStripe && (!walletButtonConfig || !walletButtonConfig.underForm) && (
                <WalletPaymentFC
                  planData={planData}
                  getItem={getItem}
                  setItem={setItem}
                  pageName={pageName}
                  config={config}
                  stripeButtonTheme={StripeWalletButtonTheme.dark}
                  getStripeWalletButtonStyles={paymentOptions.getStripeWalletButtonStyles}
                  successRedirectTo={successRedirectTo}
                  contactStateRoutesMap={contactStateRoutesMap}
                />
              )}

              {receiptData && selectedPlanId && (
                <Receipt receiptData={receiptData[selectedPlanId]} receiptTitle={receiptTitle} />
              )}

              <div className="buttons-container">
                {showStripe && walletButtonConfig && walletButtonConfig.underForm && (
                  <WalletPaymentFC
                    planData={planData}
                    getItem={getItem}
                    setItem={setItem}
                    pageName={pageName}
                    config={config}
                    stripeButtonTheme={StripeWalletButtonTheme.dark}
                    getStripeWalletButtonStyles={paymentOptions.getStripeWalletButtonStyles}
                    showDelimiter={walletButtonConfig.showDelimiter}
                    buttonsDelimiterText={walletButtonConfig.delimiterText}
                    successRedirectTo={successRedirectTo}
                    contactStateRoutesMap={contactStateRoutesMap}
                  />
                )}
                {showPayPal && (
                  <div className="paypal-container">
                    <PayPalButtonFC
                      pageName={pageName}
                      paypalButtonConfigStyle={paymentOptions.paypalButtonConfigStyle}
                      productData={planData}
                      getItem={getItem}
                      setItem={setItem}
                      config={config}
                      customTagline={paypalCustomTagline}
                      successRedirectTo={successRedirectTo}
                      contactStateRoutesMap={contactStateRoutesMap}
                    />
                  </div>
                )}
              </div>
              {showStripe && (
                <StripeCheckoutFormFC
                  pageName={pageName}
                  getItem={getItem}
                  setItem={setItem}
                  planDetails={planData}
                  config={config}
                  formConfig={stripeFormConfig}
                  successRedirectTo={successRedirectTo}
                  contactStateRoutesMap={contactStateRoutesMap}
                />
              )}

              <PaymentScreenPolicy />
              <TextBlock className="subscriptions-details" text={subscriptionsDetails}></TextBlock>
            </div>
          </Elements>
        </PayPalScriptProvider>
      </div>
    )
  }
)
